import { useEffect, useState } from 'react';

const PREFIX = 'easyai.pref.';

/**
 * useState that survives reloads and navigation, for view preferences such as time windows.
 * `allowed` guards against stale or hand-edited values.
 */
export function usePersistentState<T extends string | number | boolean>(key: string, initial: T, allowed?: readonly T[]) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(PREFIX + key);
      if (raw === null) return initial;
      const parsed = JSON.parse(raw) as T;
      if (typeof parsed !== typeof initial) return initial;
      if (allowed && !allowed.includes(parsed)) return initial;
      return parsed;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(PREFIX + key, JSON.stringify(value));
    } catch {
      // Storage full or disabled: the preference just won't persist.
    }
  }, [key, value]);

  return [value, setValue] as const;
}
